import { PersonPinRounded } from '@mui/icons-material';
import { Avatar, Box, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import { RowComponentProps } from 'react-window';

import { AssignmentStatus, PieceAssetSize, TrackingNumberStatus } from '@apiContract';
import { isPieceLoading } from '@components/Capture/helper';
import { iconDefaultSx, iconErrorSx, iconWarningSx, sxCutText } from '@minimalsTheme/shared/styles';
import { BarcodeIcon } from 'src/assets/icons/BarcodeIcon';
import { SentIcon } from 'src/assets/icons/SentIcon';
import { getPieceAsset, isPieceSend } from 'src/utils/piece';

import { CaptureId, CapturePiece } from '../../../../types';
import { iconSuccessSx } from '../../styles';
import { sxAvatarBase, sxDisabled, sxSendIcon, sxSkeleton } from './styles';

const getPersonColor = (state?: AssignmentStatus, isLoading?: boolean) => {
  if (isLoading) return iconDefaultSx;
  switch (state) {
    case AssignmentStatus.RequireValidation:
      return iconWarningSx;
    case AssignmentStatus.Unassigned:
      return iconErrorSx;
    default:
      return iconSuccessSx;
  }
};

const getTrackingColor = (state?: TrackingNumberStatus, isLoading?: boolean) => {
  if (isLoading) return iconDefaultSx;
  switch (state) {
    case TrackingNumberStatus.None:
      return iconDefaultSx;
    case TrackingNumberStatus.Missing:
      return iconWarningSx;
    case TrackingNumberStatus.Duplicate:
      return iconErrorSx;
    default:
      return iconSuccessSx;
  }
};

type Props = {
  items: CapturePiece[];
  onClick: (id: CaptureId) => void;
  selected: CaptureId;
};

type SkeletonProps = {
  style: React.CSSProperties;
};

const ItemLoader = ({ style }: SkeletonProps) => (
  <Box display="flex" style={style} sx={sxSkeleton} alignItems="center" gap={2}>
    <Skeleton variant="rounded" sx={sxAvatarBase} />
    <Box flex={1}>
      <Skeleton variant="text" width="75%" />
      <Skeleton variant="text" width="55%" />
    </Box>
  </Box>
);

const PieceListItem = ({ index, style, items, onClick, selected }: RowComponentProps<Props>) => {
  const capturedPiece = items[index];

  const isSend = isPieceSend(capturedPiece?.piece);

  if (isPieceLoading(capturedPiece)) {
    return <ItemLoader style={style} />;
  }

  return (
    <ListItemButton
      selected={capturedPiece.id === selected}
      key={index}
      sx={{ ...style, p: 0, pl: 2.5, height: '88px' }}
      onClick={(e) => {
        if (e.detail === 0) return;
        onClick(capturedPiece.id);
      }}
    >
      <ListItemAvatar sx={{ ...sxAvatarBase, position: 'relative' }}>
        <Avatar
          alt="Piece"
          src={
            getPieceAsset(capturedPiece.piece?.pieceAssets || [], PieceAssetSize.Small)?.url ||
            (capturedPiece.photo instanceof File ? URL.createObjectURL(capturedPiece.photo) : capturedPiece.photo)
          }
          variant="rounded"
          slotProps={{ img: { style: { ...sxAvatarBase, background: 'black', objectFit: 'contain' } } }}
          sx={{ ...sxAvatarBase, ...(isSend ? sxDisabled : {}) }}
        />
        {isSend ? <SentIcon sx={sxSendIcon} /> : null}
      </ListItemAvatar>
      <ListItemText
        slotProps={{ primary: sxCutText, secondary: sxCutText }}
        primary={capturedPiece?.piece?.recipient?.displayName}
        secondary={capturedPiece?.piece?.recipient?.userName}
      />
      <ListItemIcon>
        <PersonPinRounded
          sx={getPersonColor(
            capturedPiece.piece?.pieceSummary?.pieceAssignmentStatus,
            capturedPiece.piece?.pieceSummary?.inProgressAutoAssign,
          )}
        />
        <BarcodeIcon
          sx={[
            getTrackingColor(
              capturedPiece.piece?.pieceSummary?.trackingNumberStatus,
              capturedPiece.piece?.pieceSummary?.inProgressTrackingNumberDetection,
            ),
            { ml: 1 },
          ]}
        />
      </ListItemIcon>
    </ListItemButton>
  );
};

export default PieceListItem;
